import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { ReduxState } from '../../../../../../AppLoader/types';
import {
  notifySuccess,
  notifyError,
} from '../../../../../../ReactToastify/containers/ContentEditToast';
import {
  startPolling,
  setCreditBalance,
  videoSucceeded,
  videoFailed,
  selectIsPolling,
  selectStockItemEdited,
  openVideoEditorModal,
  selectVideoEditorModalIsOpen,
} from '../../../../../ContentEdit/ContentEditSlice';
import { useCrossTabSync, BroadcastMessage } from './useCrossTabSync';

export interface UseVideoEditorSyncOptions {
  /**
   * Enable/disable cross-tab synchronization
   * Useful for testing or feature flags
   * @default true
   */
  enabled?: boolean;
}

export interface UseVideoEditorSyncReturn {
  /**
   * Send a message to all other tabs
   */
  sendMessage: (message: BroadcastMessage) => void;

  /**
   * Whether BroadcastChannel is supported in this browser
   */
  isSupported: boolean;
}

/**
 * Custom hook for managing video editor cross-tab synchronization.
 *
 * Handles:
 * - Job submission notifications across tabs
 * - Credit balance updates
 * - Job success/failure status
 * - Starting polling in other tabs
 *
 * @param options Configuration options
 * @returns Object containing sendMessage function and isSupported flag
 *
 * @example
 * ```typescript
 * const { sendMessage, isSupported } = useVideoEditorSync();
 *
 * // Send message to other tabs
 * sendMessage({
 *   type: 'JOB_SUBMITTED',
 *   payload: { stockItem, taskId }
 * });
 * ```
 */
export function useVideoEditorSync(
  options: UseVideoEditorSyncOptions = {}
): UseVideoEditorSyncReturn {
  const { enabled = true } = options;

  const dispatch = useDispatch<ThunkDispatch<ReduxState, unknown, AnyAction>>();
  const isPolling = useSelector(selectIsPolling);
  const isOpen = useSelector(selectVideoEditorModalIsOpen);
  const stockItemEdited = useSelector(selectStockItemEdited);

  /**
   * Callback to open the modal - used by success notification
   */
  const openEditorModal = useCallback(() => {
    if (stockItemEdited) {
      dispatch(openVideoEditorModal(stockItemEdited));
    }
  }, [stockItemEdited, dispatch]);

  /**
   * Handle incoming messages from other tabs
   */
  const handleMessage = useCallback(
    (message: BroadcastMessage) => {
      switch (message.type) {
        case 'JOB_SUBMITTED':
          // Another tab submitted a job - start polling here too
          // Only start if not already polling to avoid race conditions
          if (!isPolling) {
            dispatch(startPolling({ stockItem: message.payload.stockItem }));
          }
          break;

        case 'CREDITS_UPDATED':
          // Another tab spent/earned credits - update balance
          dispatch(setCreditBalance(message.payload.newBalance));
          break;

        case 'JOB_SUCCEEDED':
          // Another tab's job succeeded - update our state with the result
          if (!isOpen) {
            notifySuccess(openEditorModal);
          }
          dispatch(
            videoSucceeded({
              outputUrl: message.payload.outputUrl,
              stockItemEdited: message.payload.stockItemEdited,
              jobId: message.payload.jobId,
            })
          );
          break;

        case 'JOB_FAILED':
          // Another tab's job failed - update our state
          notifyError();
          dispatch(
            videoFailed({
              error: message.payload.error,
              jobId: message.payload.jobId,
            })
          );
          break;

        default:
          // Ignore unknown message types
          break;
      }
    },
    [dispatch, isPolling, isOpen, openEditorModal]
  );

  // Set up cross-tab synchronization
  const { sendMessage: sendMessageRaw, isSupported } = useCrossTabSync(
    enabled ? handleMessage : () => {} // No-op if disabled
  );

  /**
   * Wrapped sendMessage that respects the enabled flag
   */
  const sendMessage = useCallback(
    (message: BroadcastMessage) => {
      if (enabled) {
        sendMessageRaw(message);
      }
    },
    [enabled, sendMessageRaw]
  );

  return {
    sendMessage,
    isSupported,
  };
}
